import { Library, Paperclip, X } from 'lucide-react'
import { toast } from 'sonner'

import { AttachmentLibraryPicker } from '@/components/attachments/attachment-library-picker'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from '@/components/ui/shadcn-io/dropzone'
import type { AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { formatBytes } from '@/lib/file-utils'
import { cn } from '@/lib/utils'
import { EMAIL_ATTACHMENT_LIMITS } from '@/stores/attachments.store'
import type { StagedAttachment } from './email-compose-state'

interface EmailAttachmentsFieldProps {
  organizationId: string
  disabled: boolean
  attachments: StagedAttachment[]
  libraryAttachments: AttachmentLite[]
  libraryPickerOpen: boolean
  uploadedBytes: number
  combinedCount: number
  combinedBytes: number
  combinedMeterPercent: number
  overCombinedFileLimit: boolean
  overCombinedLimit: boolean
  hasUploading: boolean
  onLibraryPickerOpenChange: (open: boolean) => void
  onLibraryAttachmentsChange: (attachments: AttachmentLite[]) => void
  onFileDrop: (files: File[]) => void
  onRemoveAttachment: (id: string) => void
  onRemoveLibraryAttachment: (id: string) => void
}

export function EmailAttachmentsField({
  organizationId,
  disabled,
  attachments,
  libraryAttachments,
  libraryPickerOpen,
  uploadedBytes,
  combinedCount,
  combinedBytes,
  combinedMeterPercent,
  overCombinedFileLimit,
  overCombinedLimit,
  hasUploading,
  onLibraryPickerOpenChange,
  onLibraryAttachmentsChange,
  onFileDrop,
  onRemoveAttachment,
  onRemoveLibraryAttachment,
}: EmailAttachmentsFieldProps) {
  return (
    <>
      <div className="grid gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label>Attachments</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onLibraryPickerOpenChange(true)}
            disabled={disabled || overCombinedLimit}
          >
            <Library className="h-4 w-4" />
            Choose from library
          </Button>
        </div>
        <Dropzone
          accept={EMAIL_ATTACHMENT_LIMITS.ACCEPT}
          maxFiles={EMAIL_ATTACHMENT_LIMITS.MAX_FILES}
          maxSize={EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES}
          onDrop={onFileDrop}
          onError={(error) => toast.error(error.message)}
          disabled={
            disabled ||
            hasUploading ||
            combinedCount >= EMAIL_ATTACHMENT_LIMITS.MAX_FILES
          }
        >
          <DropzoneEmptyState>
            <div className="flex flex-col items-center justify-center">
              <Paperclip className="size-6 text-muted-foreground" />
              <p className="my-1 text-sm font-medium">Attach files</p>
              <p className="text-xs text-muted-foreground">
                Drag and drop or click · Max {EMAIL_ATTACHMENT_LIMITS.MAX_FILES}{' '}
                files, {formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)}{' '}
                total
              </p>
            </div>
          </DropzoneEmptyState>
          <DropzoneContent />
        </Dropzone>

        {(attachments.length > 0 || libraryAttachments.length > 0) && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-1 rounded-md border bg-muted/50 px-2 py-1 text-xs"
              >
                {attachment.uploading && (
                  <span className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent" />
                )}
                <span className="max-w-[140px] truncate">
                  {attachment.filename}
                </span>
                <span className="text-muted-foreground">
                  {formatBytes(attachment.sizeBytes)}
                </span>
                {!attachment.uploading && (
                  <button
                    type="button"
                    className="ml-1 text-muted-foreground hover:text-foreground"
                    onClick={() => onRemoveAttachment(attachment.id)}
                    aria-label={`Remove ${attachment.filename}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
            {libraryAttachments.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center gap-1 rounded-md border border-primary/30 bg-primary/5 px-2 py-1 text-xs"
              >
                <Library className="h-3 w-3 text-primary" />
                <span className="max-w-[140px] truncate">
                  {attachment.filename}
                </span>
                <span className="text-muted-foreground">
                  {formatBytes(attachment.sizeBytes)}
                </span>
                <button
                  type="button"
                  className="ml-1 text-muted-foreground hover:text-foreground"
                  onClick={() => onRemoveLibraryAttachment(attachment.id)}
                  aria-label={`Remove ${attachment.filename}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {combinedCount > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {combinedCount} of {EMAIL_ATTACHMENT_LIMITS.MAX_FILES} files
              </span>
              <span
                className={cn(
                  'tabular-nums',
                  overCombinedLimit
                    ? 'font-medium text-destructive'
                    : 'text-muted-foreground',
                )}
              >
                {formatBytes(combinedBytes)} /{' '}
                {formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)}
              </span>
            </div>
            <Progress
              value={combinedMeterPercent}
              className={cn(
                overCombinedLimit &&
                  '[&_[data-slot=progress-indicator]]:bg-destructive',
              )}
              aria-label="Combined attachment size"
            />
            {overCombinedLimit && (
              <p className="text-xs text-destructive" role="alert">
                {overCombinedFileLimit
                  ? `Too many files — max ${EMAIL_ATTACHMENT_LIMITS.MAX_FILES} per email.`
                  : `Attachments exceed the ${formatBytes(EMAIL_ATTACHMENT_LIMITS.MAX_TOTAL_BYTES)} limit.`}
              </p>
            )}
          </div>
        )}
      </div>

      <AttachmentLibraryPicker
        organizationId={organizationId}
        open={libraryPickerOpen}
        onOpenChange={onLibraryPickerOpenChange}
        initialSelected={libraryAttachments}
        existingCount={attachments.length}
        existingBytes={uploadedBytes}
        onConfirm={onLibraryAttachmentsChange}
      />
    </>
  )
}
